/**
 * Spec 004 FR-014, US5 — Equanaut Prime conversational surface
 *
 * PrimePage is the default authenticated landing surface in equa-web.
 * It presents the user's personal Chief of Staff (Equanaut Prime) as the
 * primary entry point, with cross-Organization context visible from one pane.
 *
 * Acceptance criteria covered:
 *   US5 AC1: Signed-in user sees Prime pane with their name and org list.
 *   US5 AC2: Prime responds inline to cross-Organization questions.
 *   US5 AC3: User can navigate into an Organization from the Prime pane.
 */

import * as React from 'react'
import { connect } from 'react-redux'
import { StateTransform } from '@src/index'
import { User, Paths, equabotzPath, fleetTrackerPath } from '@logic'
import { Link } from 'react-router-dom'
import { fetchPrimeBundle, CrossOrgSummary, PrimeInfo } from './prime-api'

interface Message {
  from: 'user' | 'prime'
  text: string
  ts: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StateProps {
  user?: User
}

type Props = StateProps

const PrimePageInner: React.FC<Props> = ({ user }) => {
  const [prime, setPrime] = React.useState<PrimeInfo | null>(null)
  const [summary, setSummary] = React.useState<CrossOrgSummary | null>(null)
  const [messages, setMessages] = React.useState<Message[]>([])
  const [inputText, setInputText] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const messagesEndRef = React.useRef<HTMLDivElement>(null)

  // Scroll to latest message
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Load Prime + cross-org summary on mount
  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchPrimeBundle()
      .then(([primeData, summaryData]) => {
        if (cancelled) return
        setPrime(primeData.prime)
        setSummary(summaryData.summary)
        // Greet the user with their org state
        const orgCount = summaryData.summary.totalOrgs
        const unreachable = summaryData.summary.unreachableOrgs
        let greeting = `Hello${user?.username ? ', ' + user.username : ''}! I'm your Equanaut Prime — your personal Chief of Staff across all your Organizations.`
        if (orgCount === 0) {
          greeting += " You have no Organizations yet. Would you like to create one?"
        } else {
          greeting += ` You have ${orgCount} Organization${orgCount !== 1 ? 's' : ''}.`
          if (unreachable > 0) {
            greeting += ` (${unreachable} currently unreachable — showing last known state.)`
          }
        }
        setMessages([{ from: 'prime', text: greeting, ts: new Date().toISOString() }])
      })
      .catch(err => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [user?.username])

  const handleSend = React.useCallback(() => {
    const text = inputText.trim()
    if (!text) return

    const userMsg: Message = { from: 'user', text, ts: new Date().toISOString() }
    setMessages(prev => [...prev, userMsg])
    setInputText('')

    // Cross-org context response (MVP: pattern-match common questions)
    setTimeout(() => {
      let response = "I'm processing your request across all your Organizations. Full AI reasoning will be wired in the next release."

      const lower = text.toLowerCase()
      if (summary && (lower.includes('status') || lower.includes('summary') || lower.includes('how many'))) {
        const lines = summary.orgs.map(o =>
          `• ${o.orgSlug} (${o.userRole})${o.status === 'unreachable' ? ' — unreachable' : ''}`
        )
        response = summary.totalOrgs === 0
          ? "You have no Organizations yet."
          : `Here's your cross-Organization status:\n${lines.join('\n')}`
      } else if (lower.includes('create') || lower.includes('new organization')) {
        response = "To create a new Organization, navigate to the Organizations section from the sidebar."
      }

      setMessages(prev => [...prev, { from: 'prime', text: response, ts: new Date().toISOString() }])
    }, 400)
  }, [inputText, summary])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading Equanaut Prime…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          Equanaut Prime is temporarily unavailable. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      {/* ── Sidebar: org list ───────────────────────────────────────────── */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <span style={styles.sidebarTitle}>My Organizations</span>
          <Link to={Paths.organizationList} style={styles.sidebarLink}>View all</Link>
        </div>
        {summary && summary.orgs.length > 0 ? (
          <ul style={styles.orgList}>
            {summary.orgs.map(org => (
              <li key={org.orgId} style={styles.orgItem}>
                <Link
                  to={`/organization/${org.orgId}`}
                  style={styles.orgLink}
                  title={org.status === 'unreachable' ? `Last known: ${org.lastKnownAt ?? 'unknown'}` : undefined}
                >
                  <span style={styles.orgSlug}>{org.orgSlug}</span>
                  <span style={styles.orgRole}>{org.userRole}</span>
                  {org.status === 'unreachable' && (
                    <span style={styles.orgUnreachable} title={`Last known: ${org.lastKnownAt ?? 'unknown'}`}>
                      ⚠
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p style={styles.noOrgs}>No Organizations yet.</p>
        )}
        <Link to={Paths.newOrganization} style={styles.newOrgButton}>+ New Organization</Link>

        {/* Sub-agents section — surfaces Equabotz and future agent fleets that answer to Prime */}
        <div style={styles.sidebarSection}>
          <div style={styles.sidebarHeader}>
            <span style={styles.sidebarTitle}>Sub-agents</span>
          </div>
          <ul style={styles.orgList}>
            <li style={styles.orgItem}>
              <Link to={equabotzPath} style={styles.orgLink}>
                <span style={styles.orgSlug}>Equabotz Gateway</span>
                <span style={styles.orgRole}>pool</span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Observability section — read-only dashboards that watch the fleet (spec 022). */}
        <div style={styles.sidebarSection}>
          <div style={styles.sidebarHeader}>
            <span style={styles.sidebarTitle}>Observability</span>
          </div>
          <ul style={styles.orgList}>
            <li style={styles.orgItem}>
              <Link to={fleetTrackerPath} style={styles.orgLink}>
                <span style={styles.orgSlug}>Fleet Tracker</span>
                <span style={styles.orgRole}>observer</span>
              </Link>
            </li>
          </ul>
        </div>
      </aside>

      {/* ── Main: Prime chat pane ──────────────────────────────────────── */}
      <main style={styles.main}>
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.primeAvatar}>✦</span>
            <div>
              <div style={styles.primeName}>Equanaut Prime</div>
              <div style={styles.primeSubtitle}>
                {user?.username ? `${user.username}'s` : 'Your'} personal Chief of Staff
                {prime && <span style={styles.primeStatus}> · {prime.status}</span>}
              </div>
            </div>
          </div>
        </header>

        <div style={styles.messageList}>
          {messages.map((msg, i) => (
            <div
              key={i}
              style={msg.from === 'prime' ? styles.messagePrime : styles.messageUser}
            >
              {msg.from === 'prime' && <span style={styles.primeLabel}>✦ Prime</span>}
              <p style={styles.messageText}>{msg.text}</p>
              <span style={styles.messageTime}>
                {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div style={styles.inputArea}>
          <textarea
            style={styles.textarea}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Prime anything across your Organizations…"
            rows={2}
          />
          <button
            style={styles.sendButton}
            onClick={handleSend}
            disabled={!inputText.trim()}
          >
            Send
          </button>
        </div>
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Minimal inline styles (no external CSS dependency)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 60px)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    background: '#f8f9fa',
  },
  loading: {
    padding: '40px',
    color: '#666',
    fontSize: '16px',
  },
  error: {
    padding: '40px',
    color: '#c0392b',
    fontSize: '14px',
    lineHeight: '1.6',
  },
  // Sidebar
  sidebar: {
    width: '260px',
    minWidth: '200px',
    background: '#fff',
    borderRight: '1px solid #e9ecef',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    overflowY: 'auto',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px 12px',
    borderBottom: '1px solid #e9ecef',
  },
  sidebarSection: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid #e9ecef',
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  sidebarLink: {
    fontSize: '12px',
    color: '#4a90d9',
    textDecoration: 'none',
  },
  orgList: {
    listStyle: 'none',
    margin: 0,
    padding: '8px 0',
    flex: 1,
  },
  orgItem: {
    margin: 0,
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    textDecoration: 'none',
    color: '#343a40',
    fontSize: '14px',
    transition: 'background 0.15s',
  },
  orgSlug: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  orgRole: {
    fontSize: '11px',
    color: '#868e96',
    background: '#f1f3f5',
    padding: '2px 6px',
    borderRadius: '10px',
  },
  orgUnreachable: {
    fontSize: '12px',
    color: '#e67e22',
  },
  noOrgs: {
    padding: '16px',
    color: '#868e96',
    fontSize: '14px',
  },
  newOrgButton: {
    display: 'block',
    margin: '12px 16px 0',
    padding: '8px 12px',
    background: '#4a90d9',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    textAlign: 'center',
  },
  // Main chat
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  primeAvatar: {
    fontSize: '28px',
    color: '#4a90d9',
    lineHeight: 1,
  },
  primeName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#212529',
  },
  primeSubtitle: {
    fontSize: '12px',
    color: '#868e96',
  },
  primeStatus: {
    color: '#40c057',
  },
  messageList: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  messagePrime: {
    alignSelf: 'flex-start',
    maxWidth: '70%',
    background: '#fff',
    border: '1px solid #e9ecef',
    borderRadius: '12px 12px 12px 0',
    padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  messageUser: {
    alignSelf: 'flex-end',
    maxWidth: '70%',
    background: '#4a90d9',
    borderRadius: '12px 12px 0 12px',
    padding: '12px 16px',
    color: '#fff',
  },
  primeLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: 600,
    color: '#4a90d9',
    marginBottom: '4px',
    letterSpacing: '0.5px',
  },
  messageText: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
  },
  messageTime: {
    display: 'block',
    fontSize: '10px',
    color: '#adb5bd',
    marginTop: '6px',
    textAlign: 'right',
  },
  inputArea: {
    display: 'flex',
    gap: '8px',
    padding: '16px 24px',
    background: '#fff',
    borderTop: '1px solid #e9ecef',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    resize: 'none',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    lineHeight: '1.5',
  },
  sendButton: {
    padding: '10px 20px',
    background: '#4a90d9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s',
    height: '42px',
  },
}

// ---------------------------------------------------------------------------
// Redux connect
// ---------------------------------------------------------------------------

const mapStateToProps: StateTransform<StateProps> = s => ({
  user: s.user,
})

export const PrimePage = connect(mapStateToProps)(PrimePageInner)
export default PrimePage
